import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Spin } from "antd";
import { RootState } from "../../store";
import { baseUrl } from "../../constants/api";
import "./QuizReview.css";

interface ReviewQuestion {
    question_id: string;
    question: string;
    options: string[];
    correct_option_id: number;
    marked_option_id: number | null;
    is_correct: boolean;
}

interface QuizReviewData {
    quiz_name: string;
    quiz_id: string;
    total_questions: number;
    score: number;
    correct_count: number;
    incorrect_count: number;
    unattempted_count: number;
    created_at: string;
    attempted_on: string;
    questions: ReviewQuestion[];
}

const QuizReview: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const token = useSelector((state: RootState) => state.account.token);
    const [searchParams] = useSearchParams();
    const userId = searchParams.get("user_id");
    const [reviewData, setReviewData] = useState<QuizReviewData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchReviewData = async () => {
            try {
                setLoading(true);
                let apiUrl = `${baseUrl}/api/quiz-review/${id}`;
                if (userId) {
                    apiUrl += `?user_id=${userId}`;
                }
                const response = await fetch(apiUrl, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setReviewData(data);
                } else {
                    const errData = await response.json();
                    setError(errData.message || "Failed to load quiz review");
                }
            } catch (err) {
                setError("An error occurred while loading the quiz review");
            } finally {
                setLoading(false);
            }
        };

        if (id && token) {
            fetchReviewData();
        }
    }, [id, token]);

    const formatDate = (dateStr: string) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = String(d.getFullYear()).slice(-2);
        return `${day}-${month}-${year}`;
    };

    const getOptionLabel = (index: number): string => {
        return String.fromCharCode(97 + index) + ".";
    };

    const getOptionClass = (
        question: ReviewQuestion,
        optionIndex: number
    ): string => {
        const isCorrectOption = optionIndex === question.correct_option_id;
        const isMarkedOption = optionIndex === question.marked_option_id;

        if (question.is_correct && isCorrectOption) {
            return "quiz-review-option--correct";
        }

        if (!question.is_correct) {
            if (isMarkedOption) {
                return "quiz-review-option--incorrect";
            }
            if (isCorrectOption) {
                return "quiz-review-option--correct";
            }
        }

        return "";
    };

    const getScorePercentage = (): string => {
        if (!reviewData) return "0%";
        const pct = (reviewData.score / reviewData.total_questions) * 100;
        return `${Math.round(pct)}%`;
    };

    if (loading) {
        return (
            <div className="quiz-review-loading">
                <Spin size="large" />
            </div>
        );
    }

    if (error || !reviewData) {
        return (
            <div className="quiz-review-error">
                <div className="quiz-review-error__content">
                    <h2>Unable to load quiz review</h2>
                    <p>{error || "No data available"}</p>
                    <button onClick={() => navigate("/")} className="quiz-review-back-btn">
                        Go Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="quiz-review-page">
            {/* Header */}
            <div className="quiz-review-header">
                <div className="quiz-review-header__left">
                    <button
                        className="quiz-review-header__back"
                        onClick={() => navigate(-1)}
                        aria-label="Go back"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5" />
                            <polyline points="12 19 5 12 12 5" />
                        </svg>
                    </button>
                    <div>
                        <h1 className="quiz-review-header__title">{reviewData.quiz_name}</h1>
                        <div className="quiz-review-header__meta">
                            <span className="quiz-review-header__meta-item">
                                Questions: {reviewData.total_questions}
                            </span>
                            <span className="quiz-review-header__meta-divider">|</span>
                            <span className="quiz-review-header__meta-item quiz-review-header__meta-item--muted">
                                Attempted on: {formatDate(reviewData.attempted_on)}
                            </span>
                            <span className="quiz-review-header__meta-divider">|</span>
                            <span className="quiz-review-header__meta-item quiz-review-header__meta-item--correct">
                                Correct: {reviewData.correct_count}
                            </span>
                            <span className="quiz-review-header__meta-divider">|</span>
                            <span className="quiz-review-header__meta-item quiz-review-header__meta-item--incorrect">
                                Incorrect: {reviewData.incorrect_count}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="quiz-review-header__right">
                    <div className="quiz-review-header__score">
                        Score : {getScorePercentage()}
                    </div>
                </div>
            </div>

            {/* Questions List */}
            <div className="quiz-review-questions">
                {reviewData.questions.map((question, qIndex) => (
                    <div
                        key={question.question_id || qIndex}
                        className={`quiz-review-question ${question.is_correct
                            ? "quiz-review-question--correct"
                            : "quiz-review-question--incorrect"
                            }`}
                    >
                        {/* Question number indicator */}
                        <div
                            className={`quiz-review-question__indicator ${question.is_correct
                                ? "quiz-review-question__indicator--correct"
                                : "quiz-review-question__indicator--incorrect"
                                }`}
                        />

                        {/* Question text */}
                        <div className="quiz-review-question__text">
                            <span className="quiz-review-question__number">{qIndex + 1}.</span>
                            <span>{question.question}</span>
                        </div>

                        {/* Options grid */}
                        <div className="quiz-review-options">
                            {question.options.map((option, oIndex) => {
                                const optionClass = getOptionClass(question, oIndex);
                                return (
                                    <div
                                        key={oIndex}
                                        className={`quiz-review-option ${optionClass}`}
                                    >
                                        <span className={`quiz-review-option__label ${optionClass ? "quiz-review-option__label--highlighted" : ""}`}>
                                            {getOptionLabel(oIndex)}
                                        </span>
                                        <span className={`quiz-review-option__text ${optionClass ? "quiz-review-option__text--highlighted" : ""}`}>
                                            {option}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default QuizReview;
